import { FRAGMENT_SHADER, VERTEX_SHADER } from './noiseShader';
import type { Size } from './screenNoiseMath';

const compile = (gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
  gl.deleteShader(shader);
  return null;
};

/**
 * The screen-noise shader on one WebGL context. Holds any number of image
 * textures and renders one at a time, at a given size, into the bottom-left
 * corner of its canvas (grown as needed, never shrunk); the caller copies
 * the result out. No React, no animation loop — see compositor.ts.
 * create() returns null where WebGL (or the shader, or highp floats) is
 * unavailable, so the caller can show the plain image instead.
 */
export class ScreenNoiseRenderer {
  private readonly uniforms: { strength: WebGLUniformLocation; grain: WebGLUniformLocation; phase: WebGLUniformLocation };

  private constructor(
    private readonly gl: WebGLRenderingContext,
    private readonly program: WebGLProgram,
    private readonly buffer: WebGLBuffer,
    readonly maxSize: number,
  ) {
    this.uniforms = {
      strength: gl.getUniformLocation(program, 'u_strength')!,
      grain: gl.getUniformLocation(program, 'u_grain')!,
      phase: gl.getUniformLocation(program, 'u_phase')!,
    };
  }

  static create(canvas: HTMLCanvasElement): ScreenNoiseRenderer | null {
    // It draws one quad: no depth or stencil buffers (they would match the canvas size).
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: false,
      depth: false,
      stencil: false,
    });
    if (!gl) return null;
    // The shader needs highp floats (see noiseShader.ts).
    if (!gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.precision) return null;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    const buffer = gl.createBuffer();
    if (!vs || !fs || !program || !buffer) return null;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
    gl.useProgram(program);

    // One triangle strip covering the viewport.
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const maxSize = Math.min(gl.getParameter(gl.MAX_TEXTURE_SIZE) as number, 4096);
    return new ScreenNoiseRenderer(gl, program, buffer, maxSize);
  }

  get canvas(): HTMLCanvasElement {
    return this.gl.canvas as HTMLCanvasElement;
  }

  /**
   * Uploads a (CORS-loaded) image into a texture of its own. Non-power-of-two
   * safe: clamp, linear, no mipmaps. Throws when the browser refuses the
   * pixels (SecurityError) or no texture can be created.
   */
  upload(img: HTMLImageElement): WebGLTexture {
    const { gl } = this;
    const texture = gl.createTexture();
    if (!texture) throw new Error('screen noise: no texture');
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    } catch (error) {
      gl.deleteTexture(texture);
      throw error;
    }
    return texture;
  }

  release(texture: WebGLTexture): void {
    this.gl.deleteTexture(texture);
  }

  /**
   * Sizes the canvas exactly (shrinking too): copies out of it cost its whole
   * size, so the compositor keeps it as small as the largest visible tile.
   */
  fit({ width, height }: Size): void {
    const canvas = this.canvas;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
  }

  /** Renders `texture` at `size` device px into the canvas's bottom-left corner (growing it if needed). */
  render(texture: WebGLTexture, { width, height }: Size, phase: number, strength: number, grainPx: number): void {
    const { gl, uniforms } = this;
    const canvas = this.canvas;
    if (canvas.width < width) canvas.width = width;
    if (canvas.height < height) canvas.height = height;
    gl.viewport(0, 0, width, height);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1f(uniforms.strength, strength);
    gl.uniform1f(uniforms.grain, grainPx);
    gl.uniform1f(uniforms.phase, phase);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  dispose(): void {
    const { gl } = this;
    gl.deleteBuffer(this.buffer);
    gl.deleteProgram(this.program);
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
